import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { baseUrlV2 } from '../configs/api/api.config';

export interface IColorCodeAnalysisRequest {
  color_cards: string[];
}

export interface IColorCodeAnalysisResponse {
  diagnostics: string[];
  techniques: string[];
  hintQuestions: string[];
}

const LONG_TIMEOUT_MS = 180_000;

const longTimeoutFetch: typeof fetch = async (input, init = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), LONG_TIMEOUT_MS);
  const { signal, ...rest } = init;

  if (signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      const abortHandler = () => controller.abort();
      signal.addEventListener('abort', abortHandler);
      controller.signal.addEventListener('abort', () => signal.removeEventListener('abort', abortHandler), {
        once: true,
      });
    }
  }

  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

const baseQueryLongTimeout = fetchBaseQuery({
  baseUrl: baseUrlV2,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as { authSlice?: { token?: string } })?.authSlice?.token;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
  fetchFn: longTimeoutFetch,
});

export const colorCodeApi = createApi({
  reducerPath: 'colorCode',
  baseQuery: baseQueryLongTimeout,
  endpoints: (build) => ({
    sendColorCodeAnalysis: build.mutation<IColorCodeAnalysisResponse, IColorCodeAnalysisRequest>({
      query: (body) => ({
        url: 'color-code/color-analysis',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const { useSendColorCodeAnalysisMutation } = colorCodeApi;
